import graphql__d0fc69e82bc4c3cf2a15 from "./__generated__/SimpleMenuMainNavigation_navigationItems.graphql.js";
import graphql__219b9b43e72e4f8cca42 from "./__generated__/SimpleMenuMainNavigation_authenticationConfig.graphql.js";
import graphql__842dd076f13996140734 from "./__generated__/SimpleMenuMainNavigation_viewer.graphql.js";
import graphql__4f1783e9ee149a82298e from "./__generated__/SimpleMenuMainNavigation_platform.graphql.js";import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";
import { Composite, useCompositeState } from "reakit/Composite";
import classNames from "classnames";

import SimpleMenuNavigationItem from "./SimpleMenuNavigationItem";
import HeaderAuthenticatedUser from "../../Header/HeaderAuthenticatedUser";
import LocaleSwitch from "../../LocaleSwitch";
import Button from "../../Buttons/Button";
import { Desktop, useResponsiveFlag } from "../../Utils/ResponsiveQueries";
import { useFormatMessage } from "../../Intl/Context";

import { setBackdropOpen } from "../../../reducers/styling";

const SimpleMenuMainNavigationContext = createContext({});

export const useSimpleMenuMainNavigation = () =>
  useContext(SimpleMenuMainNavigationContext);

const SimpleMenuMainNavigation = props => {
  const authenticationConfig = useFragment(
    SimpleMenuMainNavigation_authenticationConfig,
    props.authenticationConfig
  );
  const platform = useFragment(SimpleMenuMainNavigation_platform, props.platform);
  const navigationItems = useFragment(
    SimpleMenuMainNavigation_navigationItems,
    props.navigationItems
  );
  const viewer = useFragment(SimpleMenuMainNavigation_viewer, props.viewer);
  const { primaryColor, publishedLocales } = platform;
  const { enableUserAccounts } = authenticationConfig;
  const { dispatchBackdropOpen } = props;

  const { isDesktop } = useResponsiveFlag();

  const [showMenuBackdrop, setShowMenuBackdrop] = useState(false);
  const [openNavigationItemName, setOpenNavigationItemName] = useState(null);

  const compositeState = useCompositeState();

  const t = useFormatMessage();
  const closeLabel = t(intlMessages.close, { openNavigationItemName });

  const isLargeMenu = navigationItems.length > 2;

  const mainNavigationClassName = classNames("main-navigation", {
    "main-navigation-with-authentication": isDesktop && enableUserAccounts,
    "main-navigation-with-many-items": isLargeMenu,
    "main-navigation-with-locale-switch": publishedLocales.length > 1
  });

  const wrapperClassName = classNames({
    "main-menu-navbar-wrapper": true,
    show: showMenuBackdrop
  });

  const handleBackdropCloseClicked = useCallback(() => setShowMenuBackdrop(false), []);

  const handleNavigationItemClicked = useCallback(
    navigationItem => {
      if (!navigationItem.children.length && !showMenuBackdrop) return;

      setShowMenuBackdrop(!showMenuBackdrop);
      setOpenNavigationItemName(navigationItem.name);
    },
    [showMenuBackdrop]
  );

  const dispatch = useDispatch();

  const setBackdropIsOpen = useCallback(
    isBackdropOpen => {
      if (!dispatchBackdropOpen) return;

      dispatch(setBackdropOpen(isBackdropOpen));
    },
    [dispatch, dispatchBackdropOpen]
  );

  const handleBackdropDisplay = useCallback(() => {
    setBackdropIsOpen(showMenuBackdrop);
  }, [setBackdropIsOpen, showMenuBackdrop]);

  useEffect(() => {
    handleBackdropDisplay();

    return () => setBackdropIsOpen(false);
  }, [handleBackdropDisplay, setBackdropIsOpen]);

  const contextValue = useMemo(
    () => ({
      showMenuBackdrop,
      setShowMenuBackdrop
    }),
    [showMenuBackdrop]
  );

  return (
    <SimpleMenuMainNavigationContext.Provider value={contextValue}>
      <Composite {...compositeState} as="div" className={mainNavigationClassName}>
        <div className={wrapperClassName}>
          <div
            className="navigation-backdrop bg-primary-80"
            onClick={handleBackdropCloseClicked}
          >
            <Button
              type="button"
              className="modal-close"
              iconClassName="icon-close"
              screenReaderLabel={closeLabel}
              autoFocus={true}
            />
          </div>

          <ul className="navbar-nav main-menu-navbar">
            {navigationItems.map(navigationItem => (
              <SimpleMenuNavigationItem
                key={navigationItem.id}
                navigationItem={navigationItem}
                childrenItems={navigationItem.children}
                isTopLevel={true}
                onClick={() => handleNavigationItemClicked(navigationItem)}
                primaryColor={primaryColor}
                compositeState={compositeState}
              />
            ))}
          </ul>
        </div>

        <Desktop>
          <HeaderAuthenticatedUser
            viewer={viewer}
            authenticationConfig={authenticationConfig}
          />
        </Desktop>

        {publishedLocales.length > 1 && <LocaleSwitch platform={platform} />}
      </Composite>
    </SimpleMenuMainNavigationContext.Provider>
  );
};

const SimpleMenuMainNavigation_navigationItems = graphql__d0fc69e82bc4c3cf2a15;

const SimpleMenuMainNavigation_authenticationConfig = graphql__219b9b43e72e4f8cca42;

const SimpleMenuMainNavigation_viewer = graphql__842dd076f13996140734;

const SimpleMenuMainNavigation_platform = graphql__4f1783e9ee149a82298e;

export default SimpleMenuMainNavigation;

const intlMessages = defineMessages({
  close: {
    id: "header.backdrop.close",
    defaultMessage: "Fermer le menu ({openNavigationItemName})"
  }
});
